
import { useMemo, useRef, useState } from "react";
import classes from "./shipment.module.css";

import { shipmentDetailTableColumnsConfig } from "./shipmentDetailTableColumnsConfig";
import { Table } from "../../../../component/table/Table";
import { ShipmentStatusBadge } from "../OrderDetailPage";
import { ArrowDownTrayIcon, ArrowUpTrayIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import {
  useLazyDownloadInvoiceQuery,
  useUploadInvoiceMutation,
} from "../../../../services/order/orderApiSlice";
import { toast } from "react-toastify";

export const Shipment = ({ shipment, orderId }) => {
  const [triggerDownload, { isFetching: isDownloading }] = useLazyDownloadInvoiceQuery();
  const [uploadInvoice, { isLoading: isUploading }] = useUploadInvoiceMutation();
  const fileInputRef = useRef(null);
  const [isUploaded, setIsUploaded] = useState(false);

  const hasInvoice = !!shipment?.invoice_url;

  const columnDefs = useMemo(() => {
    if (!shipment?.category) return [];
    const hasVrp = shipment.category === "VRP";
    return shipmentDetailTableColumnsConfig.shipmentDetail(null, null, hasVrp);
  }, [shipment?.category]);

  const handleDownloadInvoice = async () => {
    try {
      const res = await triggerDownload({
        orderId,
        shipment_id: shipment.shipment_id,
      }).unwrap();

      const url = res?.data?.invoice_url;
      if (!url) return;

      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    try {
      await uploadInvoice({
        orderId,
        shipment_id: shipment.shipment_id,
        file,
      }).unwrap();
      setIsUploaded(true);
    } catch (err) {
      toast.error(err?.data?.message?.displayMessage || "Upload failed");
    }
  };

  return (
    <div className={classes.box}>
      <div className={classes.topBar}>
        <div className={classes.topBar__left}>
          <h2 className={classes.title}>{shipment?.title}</h2>
          <ShipmentStatusBadge status={shipment.status} showTitle={false} />
        </div>

        <div className={classes.topBar__right}>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={handleFileChange}
          />

          <button
            type="button"
            className={isUploaded ? classes.uploadBtn__success : classes.uploadBtn}
            onClick={isUploaded ? undefined : handleUploadClick}
            disabled={isUploading}
          >
            {isUploaded ? (
              <CheckCircleIcon className={classes.btnIcon} />
            ) : (
              <ArrowUpTrayIcon className={classes.btnIcon} />
            )}
            <span>{isUploading ? "Uploading..." : isUploaded ? "Uploaded" : "Upload Invoice"}</span>
          </button>

          <button
            type="button"
            className={classes.downloadBtn}
            onClick={handleDownloadInvoice}
            disabled={!hasInvoice || isDownloading}
          >
            <ArrowDownTrayIcon className={classes.btnIcon} />
            <span>{isDownloading ? "Downloading..." : "Download Invoice"}</span>
          </button>
        </div>
      </div>

      <Table data={shipment?.items || []} columns={columnDefs} />
    </div>
  );
};


